import { useState, useRef, useEffect, useMemo } from 'react';
import moment from 'moment';
import { CreditAppDataRow } from './types';
import { exportToXlsx, exportToCsv } from './exportUtils';
import { Export, Eye } from '@phosphor-icons/react';
import { DillButton, DillTable } from '../../../../general/Dill';
import type { DillColumn } from '../../../../general/Dill';

// ─── Column helpers ───────────────────────────────────────────────────────────

const textColumn = (
  key: keyof CreditAppDataRow,
  label: string,
  options?: { defaultHidden?: boolean; width?: number }
): DillColumn<CreditAppDataRow> => ({
  key,
  label,
  width: options?.width ?? 140,
  sortable: true,
  filterable: true,
  toggleable: true,
  defaultHidden: options?.defaultHidden,
  // Rows are a fixed height, so long values (e.g. a pasted paragraph as the company name) wrap
  // to at most two lines and are cut off with an ellipsis; the full value shows on hover.
  render: (value: string) => (
    <div
      className="text-xs text-gray-500 line-clamp-2 break-words min-w-0 w-full"
      title={value || undefined}>
      {value || '—'}
    </div>
  ),
});

const numericColumn = (
  key: keyof CreditAppDataRow,
  label: string,
  width = 150
): DillColumn<CreditAppDataRow> => ({
  key,
  label,
  width,
  sortable: true,
  filterable: true,
  numeric: true,
  toggleable: true,
  headerAlign: 'right',
  render: (value: number) => {
    if (value === 0 || value == null) {
      return <div className="text-sm text-gray-300 text-right">—</div>;
    }
    return (
      <div className="text-xs font-medium text-right tabular-nums text-gray-700">
        $
        {value.toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}
      </div>
    );
  },
});

// ─── Column definitions ────────────────────────────────────────────────────────

const getColumns = (): DillColumn<CreditAppDataRow>[] => [
  textColumn('contact', 'Contact', { width: 150 }),
  textColumn('email', 'Email', { defaultHidden: true, width: 200 }),
  textColumn('company', 'Customer/Company', { width: 180 }),
  {
    key: 'createdAt',
    label: 'Created At',
    width: 150,
    sortable: true,
    filterable: true,
    toggleable: true,
    render: (value: string) => {
      if (!value) return <div className="text-xs text-gray-500">—</div>;
      return <div className="text-xs text-gray-500">{moment(value).format('MM/DD/YYYY')}</div>;
    },
  },
  textColumn('requestedBy', 'Requested By', { defaultHidden: true, width: 180 }),
  numericColumn('creditAmount', 'Amount Requested', 180),
  numericColumn('approvedAmount', 'Amount Approved', 180),
  textColumn('approvedAt', 'Approved At', { width: 120 }),
  textColumn('applicationType', 'Application Type', { defaultHidden: true, width: 150 }),
  textColumn('status', 'Status', { width: 120 }),
  textColumn('reviewer', 'Reviewer', { width: 130 }),
  textColumn('branch', 'Branch', { width: 120 }),
  textColumn('salesRep', 'Sales Rep', { defaultHidden: true, width: 130 }),
];

// ─── Component ─────────────────────────────────────────────────────────────────

interface CreditApplicationsDataTableProps {
  data: CreditAppDataRow[];
  isLoading?: boolean;
}

export const CreditApplicationsDataTable = ({
  data,
  isLoading,
}: CreditApplicationsDataTableProps) => {
  const [isExportOpen, setIsExportOpen] = useState(false);
  const [isColumnsOpen, setIsColumnsOpen] = useState(false);
  // Rows after the table's search/filters/sorting are applied — what we export.
  const [filteredData, setFilteredData] = useState<CreditAppDataRow[]>(data);
  const exportRef = useRef<HTMLDivElement>(null);
  const columnsRef = useRef<HTMLDivElement>(null);

  const allColumns = useMemo(() => getColumns(), []);

  const [hiddenCols, setHiddenCols] = useState<Set<string>>(
    () => new Set(allColumns.filter((c) => c.defaultHidden).map((c) => c.key))
  );

  const visibleColumns = useMemo(
    () => allColumns.filter((c) => !hiddenCols.has(c.key)),
    [allColumns, hiddenCols]
  );

  const toggleColumn = (key: string) => {
    setHiddenCols((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        const visibleCount = allColumns.filter((c) => !next.has(c.key)).length;
        if (visibleCount <= 3) return prev;
        next.add(key);
      }
      return next;
    });
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (exportRef.current && !exportRef.current.contains(event.target as Node)) {
        setIsExportOpen(false);
      }
      if (columnsRef.current && !columnsRef.current.contains(event.target as Node)) {
        setIsColumnsOpen(false);
      }
    };

    if (isExportOpen || isColumnsOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isExportOpen, isColumnsOpen]);

  const exportColumns = useMemo(
    () =>
      visibleColumns.map((c) => ({
        key: c.key,
        label: typeof c.label === 'string' ? c.label : c.key,
      })),
    [visibleColumns]
  );

  const handleExportXlsx = () => {
    exportToXlsx(filteredData, exportColumns);
    setIsExportOpen(false);
  };

  const handleExportCsv = () => {
    exportToCsv(filteredData, exportColumns);
    setIsExportOpen(false);
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 flex flex-col ">
      <div className="mb-4">
        <p className="text-sm text-gray-500">
          Find credit application reports below, where you can filter, sort, and adjust columns.
          Export to Excel or CSV.
        </p>
      </div>

      <DillTable<CreditAppDataRow>
        columns={visibleColumns}
        data={data}
        onProcessedDataChange={setFilteredData}
        rowKey={(r) => r.id}
        verticalDividers
        search
        searchPlaceholder="Search applications…"
        pagination
        pageSize={25}
        // Tall enough for two lines of text-xs (see textColumn).
        rowHeight={48}
        rowPadding={6}
        scrollX
        showActiveFilters
        striped={false}
        maxHeight="calc(100vh - 280px)"
        showFooter={false}
        loading={isLoading}
        loadingText="Loading credit applications..."
        emptyMessage="No applications match your filters"
        showCount={false}
        toolbarRight={
          <div className="flex gap-2">
            {/* Column visibility dropdown */}
            <div className="relative" ref={columnsRef}>
              <DillButton
                label="Columns"
                onClick={() => setIsColumnsOpen(!isColumnsOpen)}
                icon={<Eye size={14} />}
                variant="ghost"
              />
              {isColumnsOpen && (
                <div className="absolute right-0 z-50 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 py-2 w-56 max-h-[400px] overflow-y-auto">
                  <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-100 flex items-center justify-between">
                    <span>Show/Hide Columns</span>
                    <button
                      onClick={() => setHiddenCols(new Set())}
                      className="text-[10px] font-semibold text-blue-600 hover:text-blue-700 normal-case tracking-normal">
                      Select All
                    </button>
                  </div>
                  {allColumns.map((col) => {
                    const isVisible = !hiddenCols.has(col.key);
                    const canHide = isVisible && visibleColumns.length <= 3;
                    return (
                      <label
                        key={col.key}
                        className={`flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 ${
                          canHide ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
                        }`}>
                        <input
                          type="checkbox"
                          checked={isVisible}
                          onChange={() => toggleColumn(col.key)}
                          disabled={canHide}
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-100/80 disabled:opacity-50"
                        />
                        <span className="flex-1">{col.label}</span>
                        {canHide && (
                          <span className="text-[10px] text-gray-400">Min 3 required</span>
                        )}
                      </label>
                    );
                  })}
                </div>
              )}
            </div>

            <div
              className="relative"
              ref={exportRef}
              title={isLoading ? 'Still loading applications…' : undefined}>
              <DillButton
                label="Export"
                onClick={() => setIsExportOpen(!isExportOpen)}
                icon={<Export size={14} />}
                variant="primary"
                isDisabled={isLoading}
              />
              {isExportOpen && (
                <div className="absolute right-0 z-50 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 py-1 w-40">
                  <button
                    onClick={handleExportXlsx}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-100/80 focus:text-blue-600">
                    Export as XLSX
                  </button>
                  <button
                    onClick={handleExportCsv}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-100/80 focus:text-blue-600">
                    Export as CSV
                  </button>
                </div>
              )}
            </div>
          </div>
        }
      />
    </div>
  );
};
